import { Image } from 'components/image';
import { SMALL_DEVICE_MAX_WIDTH } from 'shared/consts';
import styled from 'styled-components';
import { DIMENSIONS } from '@hqo/react-components-library/dist/utils';
import { RESOURCE_SECTIONS_Z_INDEXES } from 'pages/resource/constants';
import HtmlText from 'shared/components/HtmlText/HtmlText';

export const HoursContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledMobileHeaderWrapper = styled.div`
  z-index: ${RESOURCE_SECTIONS_Z_INDEXES.HEADER_AND_FOOTER};
`;

export const Container = styled.div<{ showResourceDetailsDateTimeInputs?: boolean; isKioskMode?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: ${({ isKioskMode }) => (isKioskMode ? 'column' : 'row')};
  justify-content: space-around;
  flex: 1;
  padding: ${({ isKioskMode }) => (!isKioskMode ? '34px 52px' : 'none')};
  overflow-y: auto;

  @media only screen and (max-width: ${SMALL_DEVICE_MAX_WIDTH}px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    ${({ showResourceDetailsDateTimeInputs }) =>
      showResourceDetailsDateTimeInputs ? 'padding: 0;' : 'padding: 0 20px;'}
  }
`;

export const KioskContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  flex: 1;
  overflow-y: auto;
`;

export const Title = styled.h1`
  margin: 0;
  font: 700 36px/36px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};

  @media only screen and (max-width: ${SMALL_DEVICE_MAX_WIDTH}px) {
    color: ${({ theme }) => theme.colors.fontColor};
    font: 700 24px/24px ${({ theme }) => theme.fonts.join()};
  }
`;

export const Subtitle = styled.h2`
  font: 500 14px/14px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const SectionHeader = styled.div`
  display: block;
  font: 600 16px/24px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
  margin: 0 0 16px 0;
`;

export const Section = styled.div<{ hidden?: boolean }>`
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
  margin: 30px 0;
`;

export const AvailabilityDay = styled.div`
  display: flex;
  flex-direction: row;
  margin: 8px 0;
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const DayText = styled.div`
  width: 60px;
  font: 400 14px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const HoursRangeText = styled.div`
  font: 500 14px/16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const IconBlockContainer = styled.div`
  margin: 30px 0;
`;

export const StyledImage = styled(Image)<{ showResourceDetailsDateTimeInputs?: boolean }>`
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: auto;

  @media only screen and (max-width: ${SMALL_DEVICE_MAX_WIDTH}px) {
    width: 100vw;
    margin-left: 50%;
    transform: translateX(-50%);
    ${({ showResourceDetailsDateTimeInputs }) =>
      showResourceDetailsDateTimeInputs &&
      `
      position: fixed;
      z-index: ${RESOURCE_SECTIONS_Z_INDEXES.IMAGE_SECTION};
    `}
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 15px 15px;

  /* Normalize IconTextBlock icon/text alignment */
  & > div {
    align-items: center;
  }

  .text-block-icon {
    width: 1.25em;
    text-align: center;
    flex-shrink: 0;
  }
`;

export const ResourceContainer = styled.div<{ showResourceDetailsDateTimeInputs?: boolean }>`
  width: min(700px, 100%);
  padding-bottom: 90px;

  &:last-child {
    margin-bottom: 50px;
  }

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    padding-bottom: 90px;
    ${({ showResourceDetailsDateTimeInputs }) => showResourceDetailsDateTimeInputs && 'position: relative;'}
  }
`;

export const Content = styled(HtmlText)`
  color: ${({ theme }) => theme.colors.fontColor};
  font-size: 14px;
`;

export const CalendarViewOnlyText = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
  font: italic 400 14px/20px ${({ theme }) => theme.fonts.join()};
`;

export const BackLinkWrapper = styled.div`
  margin-bottom: 34px;

  @media only screen and (max-width: ${SMALL_DEVICE_MAX_WIDTH}px) {
    margin: 11px 0;
  }
`;

export const StyledDot = styled.div<{ showResourceDetailsDateTimeInputs?: boolean }>`
  margin: 10px 4px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${({ theme, showResourceDetailsDateTimeInputs }) =>
    showResourceDetailsDateTimeInputs ? theme.colors.palette.systemBlack : theme.colors.$secondary};
`;

export const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

/**
 * The title with the integration badge to its right, vertically centred on the title's line box so
 * the badge sits mid-cap-height, above the baseline, like a tag. Carries the 30px top offset the title
 * used to own so the badge shares it. Wraps so a long name on a narrow screen pushes the badge to the
 * next line rather than squeezing the title.
 */
export const TitleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-top: 30px;
  min-width: 0;
`;
